import { useState, type ChangeEvent, type FormEvent } from "react";

import {
  classifyPromotionFailure,
  isMovedPrecondition,
  useRequestPromotionMutation,
  type ApprovalEvidence,
  type PromotionFailure,
} from "@/api/publication";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Field, Textarea } from "@/ui/Input";
import { cn } from "@/lib/cn";

import {
  MANAGER_EVIDENCE_MEDIA_TYPE,
  validateManagerEvidence,
} from "./managerEvidence";

/**
 * The post-merge handoff, rung 4 of the chronology #437 ratified (#445).
 *
 * The clinician's note and the signed approval document are collected HERE and
 * nowhere else. #437's rule is explicit — rung 1 requests publication with "no
 * note/PDF precondition", and the release-team evidence is gathered only once a
 * release owner has approved externally and the approved version has been taken
 * in. Asking for either earlier would collect evidence for a handoff that may
 * never be mintable, and #358's boundary would refuse it anyway: the
 * `approvalId` this binds does not exist until then.
 *
 * NOT A MANAGER-APPROVAL CONTROL, and the distinction is the amended UX's
 * (#203). Nothing here selects, submits or receives an approval: the approval
 * was observed in GitHub by the #49 reconciler, published by #444, and the only
 * thing this dialog does with it is carry `approvalId` back verbatim. The
 * document it uploads is Editor-side evidence and never counts as approval —
 * the ticket says so and the service enforces it, since a handoff whose named
 * approval is unknown, dismissed or covers a different head answers
 * `approval_not_bindable` however good the PDF is.
 *
 * ## Why the idempotency key belongs to the DIALOG rather than to the press
 *
 * It is minted once, when the dialog opens, and reused by every attempt inside
 * it. That is what makes the contract's replay real: "same key + same bytes
 * replays, same key + different bytes conflicts". A key minted per press would
 * turn a retry of a request that timed out mid-flight into a second handoff,
 * which is precisely the duplicate the key exists to prevent.
 *
 * It also makes `idempotency_conflict` reachable and, more importantly,
 * ACTIONABLE: change the note or the document after an attempt was already
 * spent and the service refuses rather than silently minting something
 * different from what the first attempt sent. The recovery is stated in the
 * copy — close this and start again, which mints a fresh key.
 */

/**
 * One sentence per refusal, and no two the same.
 *
 * `satisfies` gives exhaustiveness at compile time — a new member of
 * `PromotionFailure` fails the build rather than rendering blank — and the
 * export gives DISTINCTNESS at runtime, which no type can express. Both halves
 * matter here: this surface's whole job on a refusal is telling a clinician
 * which of twelve different things went wrong and what each one asks of them,
 * and two members quietly sharing a sentence would collapse that back into the
 * generic "try again" the classification exists to avoid. Exported for the same
 * reason `CLONE_NEVER_COPIES` is: the property is worth asserting, and asserting
 * it needs the value.
 */
export const HANDOFF_FAILURE_COPY = {
  evidence_not_pdf:
    "That file is not a PDF. Choose the signed approval document, saved as a PDF.",
  evidence_too_large:
    "That document is larger than 10 MB. Choose a smaller file.",
  evidence_corrupt:
    "That document did not arrive complete. Choose the file again and resend it.",
  invalid:
    "Add a short note of up to 500 characters, and choose the signed approval document.",
  idempotency_conflict:
    "This attempt was already sent with a different note or document. Close this and start again to send the new one.",
  // Fail-closed, and deliberately not phrased as a retry. The approval this
  // would have bound is unknown, withdrawn, or no longer covers this version,
  // and pressing the button again cannot change any of those.
  approval_not_bindable:
    "This approval can no longer be used. Close this and check approval progress again.",
  not_merged:
    "This version is not ready for the last step yet. Close this and check approval progress again shortly.",
  not_evidence_eligible:
    "This version cannot be published. Freeze a new version once the approved safety-rule catalogue is in place.",
  candidate_mismatch:
    "This version changed since approval progress was read. Close this and check it again before sending.",
  denied: "You are not authorized to complete this step for this Project.",
  unavailable:
    "Completing this step is not available in this environment yet.",
  error: "The note and document could not be sent. Try again.",
} satisfies Record<PromotionFailure["kind"], string>;

const NOTE_MAX_LENGTH = 500;

export function PublicationHandoffDialog({
  projectId,
  evidence,
  onClose,
  onCompleted,
  onPreconditionMoved,
}: {
  projectId: string;
  /**
   * The reading that offered this action, whole.
   *
   * The candidate and the approval are taken from HERE rather than from the
   * lane's newest candidate: the handoff binds the publication's own candidate,
   * and an approval the browser chose would be an approval fact the browser
   * asserted.
   */
  evidence: ApprovalEvidence;
  onClose: () => void;
  onCompleted: () => void;
  /**
   * A refusal proved the preconditions moved, so the evidence that offered this
   * action is stale. The owner refetches, the reading changes, and the action
   * withdraws itself — without a reload.
   */
  onPreconditionMoved: () => void;
}) {
  const promotion = useRequestPromotionMutation(projectId);

  const [note, setNote] = useState("");
  const [document, setDocument] = useState<File | null>(null);
  const [failure, setFailure] = useState<PromotionFailure | null>(null);
  /**
   * Minted once per dialog, never per attempt — see the header. `useState` with
   * an initializer rather than `useMemo`, because a key is state that must
   * survive every render of this dialog, and `useMemo` is a cache the runtime
   * is free to drop.
   */
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const busy = promotion.isPending;
  const ready = note.trim().length > 0 && document !== null;

  function handleDocumentChange(event: ChangeEvent<HTMLInputElement>) {
    setDocument(event.target.files?.[0] ?? null);
    // The refusal on screen was about the previous file. Keeping it beside a
    // new one would report a problem the clinician has already acted on.
    setFailure(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || busy || document === null) {
      return;
    }
    setFailure(null);

    // Fail fast, in the service's own order, so an oversized or wrong-typed
    // document is refused before it is uploaded. The service re-checks all of
    // it and remains the authority.
    const refusal = await validateManagerEvidence(document);
    if (refusal !== null) {
      setFailure(refusal);
      return;
    }

    promotion.mutate(
      {
        candidate: evidence.candidate,
        // Non-null by construction: the action is offered only on an
        // `approved` + `bindable` reading, and the contract carries the
        // approval row's id on exactly those. The fallback keeps this total
        // rather than asserting — a generated type describes a contract, it
        // does not validate a response — and an empty id is refused by the
        // service as `approval_not_bindable`, which is the fail-closed
        // direction.
        approvalId: evidence.approvalId ?? "",
        note: note.trim(),
        idempotencyKey,
        managerEvidence: document,
      },
      {
        onSuccess() {
          onCompleted();
        },
        onError(error) {
          const classified = classifyPromotionFailure(error);
          setFailure(classified);
          if (isMovedPrecondition(classified)) {
            onPreconditionMoved();
          }
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // A non-idempotent request is in flight and dismissing would neither
        // cancel it nor report what happened, so escape and the overlay are
        // refused while it is — the same rule `DialogContent`'s `busy` states
        // for its close control.
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title="Send the publication note and approval"
        description="The release team needs a short note and the signed approval document before this version can be released."
        busy={busy}
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="publication-handoff-form"
              disabled={!ready || busy}
            >
              {busy ? "Sending…" : "Send to the release team"}
            </Button>
          </>
        }
      >
        <form
          id="publication-handoff-form"
          onSubmit={(event) => void handleSubmit(event)}
          className="space-y-4"
        >
          <Field
            label="Your note"
            description={`Why this version should be released. Up to ${NOTE_MAX_LENGTH} characters, kept with the record as evidence.`}
          >
            {({ inputId, describedBy }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                rows={4}
                maxLength={NOTE_MAX_LENGTH}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                  setFailure(null);
                }}
              />
            )}
          </Field>

          <Field
            label="Signed approval document"
            description="A PDF of the approval, up to 10 MB. It is stored with the record and is never published."
          >
            {({ inputId, describedBy }) => (
              <input
                id={inputId}
                aria-describedby={describedBy}
                type="file"
                accept={MANAGER_EVIDENCE_MEDIA_TYPE}
                onChange={handleDocumentChange}
                className={cn(
                  "block w-full rounded-md border border-input bg-card text-sm text-foreground",
                  "file:mr-3 file:cursor-pointer file:rounded-l-md file:border-0 file:bg-secondary",
                  "file:px-3 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                )}
              />
            )}
          </Field>

          {failure ? (
            <p role="alert" className="text-sm text-destructive">
              {HANDOFF_FAILURE_COPY[failure.kind]}
            </p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
